import { Table, type TableColumn } from '@today/ui-components'
import { Button, Dialog } from '@today/ui-primitives'
import type { ReactElement } from 'react'
import type { ErasureDetailView, ErasurePreviewRowView, Translate } from './compliance-types'
import { FormAlert } from './form-parts'

/**
 * Props for {@link ErasureRequestPanelView}. Each action callback is present only
 * when the request's status allows it AND the viewer holds its permission.
 *
 * @property request - The request, or null while it loads.
 * @property message - The last action's refusal, or null.
 * @property confirmOpen - True while the confirmation dialog is shown.
 * @property onConfirm - Opens the confirmation dialog (does not call the API).
 * @property onConfirmAccept - The dialog's confirm: the only path to the confirm route.
 */
export interface ErasureRequestPanelViewProps {
	t: Translate
	request: ErasureDetailView | null
	message: string | null
	confirmOpen: boolean
	onConfirm?: () => void
	onConfirmAccept: () => void
	onConfirmDismiss: () => void
	onCancel?: () => void
	onDownloadCertificate?: () => void
	onClose: () => void
}

const previewColumns = (t: Translate): TableColumn<ErasurePreviewRowView>[] => [
	{ key: 'category', header: t('panel.preview.category') },
	{ key: 'count', header: t('panel.preview.records') },
	{ key: 'action', header: t('panel.preview.action') },
]

function Detail({ label, children }: { label: string; children: string }): ReactElement {
	return (
		<div className="flex gap-2 text-ds-sm">
			<dt className="font-medium text-ds-text">{label}</dt>
			<dd className="break-all text-ds-text">{children}</dd>
		</div>
	)
}

function RequestBody({ t, request, ...props }: ErasureRequestPanelViewProps & { request: ErasureDetailView }) {
	return (
		<>
			<dl className="flex flex-col gap-1">
				<Detail label={t('panel.customer')}>{request.customerId}</Detail>
				<Detail label={t('panel.status')}>{request.statusLabel}</Detail>
				{request.certificateSha256 ? (
					<Detail label={t('panel.certificateSha256')}>{request.certificateSha256}</Detail>
				) : null}
			</dl>
			{request.progress ? <p className="text-ds-sm text-ds-text">{request.progress}</p> : null}
			{request.preview ? (
				<Table aria-label={t('panel.preview.title')} columns={previewColumns(t)} rows={request.preview} />
			) : null}
			<FormAlert message={props.message} />
			<div className="flex gap-2">
				{props.onConfirm ? <Button onPress={props.onConfirm}>{t('panel.confirm')}</Button> : null}
				{props.onCancel ? (
					<Button variant="muted" onPress={props.onCancel}>
						{t('panel.cancel')}
					</Button>
				) : null}
				{props.onDownloadCertificate ? (
					<Button variant="muted" onPress={props.onDownloadCertificate}>
						{t('panel.certificate')}
					</Button>
				) : null}
			</div>
			<Dialog
				isOpen={props.confirmOpen}
				onOpenChange={(open) => (open ? undefined : props.onConfirmDismiss())}
				title={t('panel.confirmTitle')}
			>
				<p className="text-ds-sm text-ds-text">{t('panel.confirmBody', { graceDays: request.graceDays })}</p>
				<Button onPress={props.onConfirmAccept}>{t('panel.confirm')}</Button>
			</Dialog>
		</>
	)
}

/** One erasure request: status, step progress, preview, guarded confirm, cancel and certificate (organism). */
export function ErasureRequestPanelView(props: ErasureRequestPanelViewProps): ReactElement {
	const { t } = props
	const title = t('panel.title')
	return (
		<section aria-label={title} className="flex flex-col gap-3 rounded-ds-md border border-ds-border p-ds-4">
			<header className="flex items-center justify-between">
				<h2 className="text-ds-lg font-semibold text-ds-text">{title}</h2>
				<Button variant="muted" size="sm" onPress={props.onClose}>
					{t('common.close')}
				</Button>
			</header>
			{props.request ? (
				<RequestBody {...props} request={props.request} />
			) : (
				<p className="text-ds-sm text-ds-text">{t('common.loading')}</p>
			)}
		</section>
	)
}
